import React, { useEffect, useState, useMemo } from 'react';
import { Form, Button, Table, Space, Typography, message, Divider, Card } from 'antd';
import { SearchOutlined, SaveOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import CommonCheckbox from '../../../global/checkbox';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedVenueLeadPro = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();
    const [locations, setLocations] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);

    const DESTINATION_VENUELEADPRO = SOURCES.DESTINATION_VENUELEADPRO || "venu_lead_pro";

    // Mutation for fetching locations
    const { mutate: getLocations, isPending: isLocLoading } = useGenericMutation({
        endpoint: "integration/venueLeadPro-locations",
        method: "POST",
        onSuccess: (data) => {
            setLocations(data || []);
            message.success("Locations fetched successfully");
        },
        onError: () => message.error("Error getting Venue Lead Pro locations")
    });

    // Mutation for saving configuration
    const { mutate: saveVenueLeadPro, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => {
            message.success("Venue Lead Pro configuration saved successfully");
        }
    });

    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_VENUELEADPRO);
            if (config) {
                form.setFieldsValue({
                    publicKey: config.publicKey || config.secretKey, // Legacy uses secretKey for this input sometimes
                    sendLeadsLimit: config.leadSentConfig?.sentLimit || 0
                });
                if (config.locations) setSelectedLocations(config.locations);
                if (config.publicKey) getLocations({ key: config.publicKey });
                else if (config.secretKey) getLocations({ key: config.secretKey });
            }
        }
    }, [destinations, form]);

    const onFinish = (values) => {
        const payload = {
            secretKey: values.publicKey, // Saving back to secretKey as per legacy
            publicKey: values.publicKey,
            sendLeadsLimit: values.sendLeadsLimit,
            integrationCompany: DESTINATION_VENUELEADPRO,
            locations: selectedLocations,
        };
        saveVenueLeadPro(payload);
    };

    const handleLocationSelect = (id, checked) => {
        // Simple selection logic - usually restricted by plan, but we'll allow multiple in UI if returned
        if (checked) {
            setSelectedLocations(prev => [...prev, id.toString()]);
        } else {
            setSelectedLocations(prev => prev.filter(locId => locId !== id.toString()));
        }
    };

    const columns = [
        {
            title: "Select",
            dataIndex: "id",
            key: "id",
            width: 80,
            render: (id) => (
                <CommonCheckbox
                    checked={selectedLocations.includes(id.toString())}
                    onChange={(e) => handleLocationSelect(id, e.target.checked)}
                />
            )
        },
        {
            title: "Location Name",
            dataIndex: "name",
            key: "name",
            render: (text) => <Text strong>{text}</Text>
        }
    ];

    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Public Key",
            name: "publicKey",
            placeholder: "Enter Venue Lead Pro Public Key",
            required: true,
            span: 24,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: (
                <Button
                    type="dashed"
                    icon={isLocLoading ? null : <SearchOutlined />}
                    onClick={() => {
                        const key = form.getFieldValue("publicKey");
                        if (key) getLocations({ key });
                        else message.warning("Please enter Public Key first");
                    }}
                    className="h-10 w-full border-dashed hover:border-primary hover:!text-primary"
                >
                    {isLocLoading && <Spinner className={"!border-primary"} />}
                    Get Locations
                </Button>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: locations.length > 0 && (
                <div className="mb-6 animate-in slide-in-from-top-2 duration-300">
                    <Text type="secondary" className="block mb-2 text-xs font-semibold uppercase tracking-wider">Available Locations</Text>
                    <Table
                        dataSource={locations}
                        columns={columns}
                        pagination={false}
                        size="small"
                        rowKey="id"
                        className="border border-gray-100 rounded-lg overflow-hidden shadow-sm"
                    />
                </div>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Initial Lead Send Limit",
            name: "sendLeadsLimit",
            placeholder: "How many leads to send initially?",
            type: "number",
            span: 24,
        }
    ], [locations, selectedLocations, isLocLoading, form]);

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Manage your Venue Lead Pro integration settings and locations.</Text>
                </div>
            </div>

            <Card className="rounded-2xl border-none shadow-sm transition-all">
                <CommonForm
                    form={form}
                    data={formData}
                    onFinish={onFinish}
                    layoutType="vertical"
                />

                <Divider className="mb-4 border-gray-100" />

                <div className="flex justify-start">
                    <Button
                        size="large"
                        icon={isSaving ? null : <SaveOutlined />}
                        onClick={() => form.submit()}
                        className={cn(btnClassName)}
                    >
                        {isSaving && <Spinner className={"!border-white"} />}
                        Save Configuration
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default EnhancedVenueLeadPro;
